import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { CharacterService, User } from './character.service';
import { MessageService } from './message.service';
import { Logger } from './logger.service';
import { Lord, LordBase } from './lord';
import { ActivatedRoute, Router, ParamMap, Params } from '@angular/router';
import { CharacterMain, CharacterMainDialog } from './character-detail/character-detail.component';
import { MatDialog } from '@angular/material/dialog';
import { AllDialogComponent } from './all-dialog/all-dialog.component';
import { ChangelogDialogComponent, changelogIsNew } from './changelog-dialog/changelog-dialog.component';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { Subscription, interval, timer } from 'rxjs';
// Result -> game-icons.net file
const CHECK_ICON_FILES = { critical: 'laurel-crown', success: 'shield-reflect', fail: 'broken-shield', fumble: 'broken-skull' };

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppComponent implements OnInit, OnDestroy {
  list: LordBase[];
  lastChar:string;
  token:string;
  snackBarConfig = new MatSnackBarConfig();
  subscription: Subscription;
  listSubscription: Subscription;
  user: User;
  constructor (

    private router: Router,
    private arouter: ActivatedRoute,
    private service: CharacterService,
    private logger: Logger,
    private message: MessageService,
    private snackBar: MatSnackBar,
    public dialog: MatDialog,
    iconRegistry: MatIconRegistry,
    sanitizer: DomSanitizer,
    )
  {
    // Check result icons (game-icons.net, CC BY 3.0): <mat-icon svgIcon="check:critical">
    for (const name of ['critical', 'success', 'fail', 'fumble']) {
      iconRegistry.addSvgIconInNamespace('check', name,
        sanitizer.bypassSecurityTrustResourceUrl(`assets/icons/${CHECK_ICON_FILES[name]}.svg`));
    }
    this.snackBarConfig.duration = 2000;
    this.token = window.localStorage.getItem('token')
  }

  ngOnInit(): void {
    this.handleLoginRedirect();
    this.showChangelogIfNew();
    this.loadList();
    this.listSubscription = this.service.listChanged.subscribe(() => this.loadList(true));
    this.service.getUser().then( u => this.setUser(u))
    this.subscription = interval(5000).subscribe(v =>
      {if ( window.localStorage.getItem('userId') ==null &&
            window.localStorage.getItem('token') !=null
      ) {
        this.service.getUser().then( u => this.setUser(u));
      }}
    );
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.listSubscription?.unsubscribe();
  }

  loadList(forced: boolean = false) {
    this.service.getList(forced).subscribe( l => {
      this.list = l;
      window.localStorage.setItem('list', JSON.stringify(l));
    })
  }

  // The Discord login redirects back as <origin>/#token=... or <origin>/#error=...
  handleLoginRedirect() {
    const params = new URLSearchParams(window.location.hash.substring(1));
    const token = params.get('token');
    const error = params.get('error');
    if (!token && !error) {
      return;
    }
    window.history.replaceState(null, '', window.location.pathname + window.location.search);
    if (token) {
      window.localStorage.setItem('token', token);
      this.token = token;
    } else {
      this.snackBar.open(error == 'unknown_player'
        ? 'This Discord account is not a known player' : 'Discord login failed', 'Ok');
    }
  }

  loginWithDiscord() {
    window.location.href = this.service.getUrl() + 'auth/login?return=' + encodeURIComponent(window.location.origin);
  }

  setUser(u: User) {
    this.user = u;
    if(!u || u.result=='fail') {
      window.localStorage.removeItem('user')
      window.localStorage.removeItem('userId')
      window.localStorage.removeItem('userName')
    } else {
      window.localStorage.setItem('user', JSON.stringify(u))
      window.localStorage.setItem('userId', ''+u.id)
      window.localStorage.setItem('userName', u.name)
    }
  }

  isPc(c: LordBase) : boolean {
    return c.type == "pc" &&  c.role != 'Lord' && c.role != 'King';
  }

  isLoggedIn() : boolean {
    return this.user != null && this.user.result != 'fail' && this.hasToken();
  }

  myCharacters() : LordBase[] {
    if (!this.isLoggedIn() || !this.list) {
      return [];
    }
    return this.list.filter(c => c.player == this.user.id);
  }

  // The active character of the logged in user has the user's Discord id as memberid
  isActive(c: LordBase) : boolean {
    return this.isLoggedIn() && this.user.did != null && '' + c.memberid == '' + this.user.did;
  }

  getLink(c: LordBase) : string {
    return `character/${c.name}`
  }

  isLord(c: LordBase) : boolean {
    return c && (c.name.indexOf('Lord')==0 || c.name.indexOf('Sir')==0 || c.name.indexOf('King')==0);
  }

  isLady(c: LordBase) : boolean {
    return c && c.name.indexOf('Lady')==0;
  }

  isOther(c: LordBase) : boolean {
    return !this.isLady(c) && !this.isLord(c);
  }

  // Opens the changelog on the first visit after it changed
  async showChangelogIfNew() {
    try {
      const base = await this.service.getBase();
      if (base && changelogIsNew(base['changes'])) {
        this.openChangelog();
      }
    } catch { }
  }

  openChangelog() {
    this.dialog.open(ChangelogDialogComponent, { width: '560px', maxWidth: '95vw' });
  }

  openAll() {
    const list = this.list || [];
    this.dialog.open(AllDialogComponent, {
      width: '420px',
      maxWidth: '95vw',
      data: {
        Lords: list.filter(c => this.isLord(c)),
        Ladies: list.filter(c => this.isLady(c)),
        Other: list.filter(c => this.isOther(c))
      }
    });
  }

  newChar() {
    let main : CharacterMain;


    const dialogRef = this.dialog.open(CharacterMainDialog, {
      width: '500px',
      data: {main: null}
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        console.log(JSON.stringify(result))
        this.snackBar.open('Dialog ok','Ok',this.snackBarConfig);
        this.service.newChar(result).then(c => {
          this.snackBar.open('Lord created','Ok',this.snackBarConfig);
          const dbid = c && c.char ? c.char['dbid'] : null;
          if (dbid != null) {
            this.router.navigate(['chargen', dbid]);
          } else {
            this.router.navigate(['character/'+result['name']]);
          }
        })
      } else {
        this.snackBar.open('Dialog Cancelled','Ok',this.snackBarConfig);
      }
    });
  }

  loginBase(l) {
    this.message.add(`login as ${l.name}`)
    this.service.startLogin(l).then(token =>
      {
        window.localStorage.setItem('token',token.token);
        this.token = token.token;
        var command = 'token '+ token.id
        this.service.bot(command).subscribe(e => console.log(`sent "${command}" ${e}`));
      })
  }

  logout() {
    this.message.add("logout")
    window.localStorage.removeItem('token');
    window.localStorage.removeItem('userId')
    window.localStorage.removeItem('user')
    window.localStorage.removeItem('userName')
    this.token = null;
    this.user = null;
  }

  navigateTo(value){
    console.log(value);
    this.router.navigate(['/character',value]);
  }

  hasToken() {
    return window.localStorage.getItem('token')!=null;
  }
}
